import React, { useCallback, useEffect, useState } from "react";
import {
    Bell,
    Bot,
    ChevronDown,
    ChevronUp,
    DollarSign,
    FileText,
    Globe,
    Loader2,
    Mail,
    MailX,
    Save,
    Settings2,
    Shield,
    Sparkles,
    Users,
    Zap,
} from "lucide-react";
import { toast } from "sonner";
import { araciMerkez } from "@/lib/api";

const SECTIONS = [
    {
        id: "genel",
        title: "Genel otomasyon",
        icon: Zap,
        accent: "gold",
        toggles: [
            ["otomasyon_aktif", "Aracı otomasyonu (ana anahtar)", "Kapalıyken hiçbir otomatik bildirim oluşturulmaz."],
            ["bildirim_panel", "Panel bildirimleri", "Aracı portalında görünen bildirim kayıtları."],
            ["bildirim_email", "E-posta bildirimleri (global)", "KAPALI iken hiçbir aracıya mail gitmez. Açmak için işaretleyip Kaydet."],
            ["bildirim_sms", "SMS bildirimleri", "Netgsm ile aracı SMS (hazırlık)."],
            ["hakedis_otomatik_hesap", "Hakediş otomatik senkron", "Ödeme/müvekkil değişince hakediş satırları güncellenir."],
        ],
    },
    {
        id: "eposta",
        title: "E-posta koruması",
        icon: Shield,
        accent: "rose",
        toggles: [
            [
                "mbshukuk_domain_mail_engelle",
                "@mbshukuk.com e-posta engeli",
                "Giriş-only ofis adreslerine mail gitmez — bounce önlenir. Panel girişi etkilenmez.",
            ],
        ],
    },
    {
        id: "olaylar",
        title: "Olay bazlı bildirimler",
        icon: Bell,
        accent: "violet",
        toggles: [
            ["olay_hakedis_odendi", "Hakediş ödendi", "Komuta'dan ödendi işaretlenince aracıya gider."],
            ["olay_lead_onay", "Lead onaylandı", "Dosya önerisi onayında bildirim."],
            ["olay_lead_red", "Lead reddedildi", "Red sebebi ile birlikte."],
            ["olay_filo_dosya", "Filo → sigorta dosyası", "Hasar dosyaya dönüştüğünde aracılara."],
            ["olay_evrak_eksik", "Eksik evrak hatırlatma", "Şablonlu evrak uyarıları."],
            ["olay_toplu_bildirim", "Manuel / toplu gönderim", "Bildirim gönder sayfasından."],
        ],
    },
    {
        id: "sablonlar",
        title: "E-posta şablon kanalları",
        icon: Mail,
        accent: "sky",
        toggles: [
            ["email_odeme_bilgi", "Ödeme kaydı / hakediş", "Konu: Ödeme kaydı — {dosya_no}"],
            ["email_yeni_dosya", "Yeni dosya", "Yeni sigorta dosyası açıldığında."],
            ["email_evrak_eksik", "Eksik evrak", "Evrak tamamlama hatırlatması."],
            ["email_lead_onay", "Lead onay e-postası", "Onaylandı mesajı."],
            ["email_lead_red", "Lead red e-postası", "Red gerekçesi."],
            ["email_filo_dosya", "Filo dosya e-postası", "Hasar → dosya dönüşümü."],
            ["email_toplu_gonderim", "Toplu / özel gönderim", "Manuel kampanya mailleri."],
        ],
    },
    {
        id: "portal",
        title: "Portal & Akıl",
        icon: Globe,
        accent: "emerald",
        toggles: [
            ["portal_aktif", "Aracı portalı", "mbshukuk.com/araci giriş ve panel."],
            ["akil_araci_aktif", "Aracı Akıl asistanı", "Portal içi AI sohbet."],
            ["referans_link_aktif", "Referans linkleri", "AR{id} başvuru linkleri."],
            ["lead_otomatik_muvekkil", "Lead → otomatik müvekkil", "Onayda telefon eşleşmezse yeni müvekkil oluştur."],
        ],
    },
];

function ToggleRow({ field, label, hint, ayarlar, onChange, accent }) {
    const border =
        accent === "gold"
            ? "border-[#D4AF37]/25 bg-[#D4AF37]/5 hover:border-[#D4AF37]/40"
            : accent === "rose"
              ? "border-rose-500/25 bg-rose-500/5 hover:border-rose-500/40"
              : accent === "violet"
                ? "border-violet-500/25 bg-violet-500/5 hover:border-violet-500/40"
                : accent === "sky"
                  ? "border-sky-500/25 bg-sky-500/5 hover:border-sky-500/40"
                  : "border-emerald-500/25 bg-emerald-500/5 hover:border-emerald-500/40";

    return (
        <label
            className={`flex items-start gap-3 text-sm cursor-pointer rounded-xl border px-3 py-2.5 transition-colors ${border}`}
        >
            <input
                type="checkbox"
                checked={!!ayarlar[field]}
                onChange={(e) => onChange(field, e.target.checked)}
                className="mt-0.5 rounded border-white/20 accent-[#D4AF37]"
            />
            <span>
                <span className="text-slate-100 block text-xs font-medium">{label}</span>
                {hint && <span className="text-[10px] text-slate-500 block mt-0.5 leading-snug">{hint}</span>}
            </span>
        </label>
    );
}

function StatPill({ label, value, warn }) {
    return (
        <div
            className={`rounded-xl border px-3 py-2 text-center ${
                warn ? "border-amber-500/30 bg-amber-500/10" : "border-white/10 bg-black/25"
            }`}
        >
            <div className={`text-lg font-bold font-mono ${warn ? "text-amber-300" : "text-[#F3E5AB]"}`}>{value}</div>
            <div className="text-[9px] uppercase tracking-wider text-slate-500 mt-0.5">{label}</div>
        </div>
    );
}

export default function AraciKomutaAyarlar({ onSaved }) {
    const [ayarlar, setAyarlar] = useState(null);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [bulkBusy, setBulkBusy] = useState("");
    const [openSections, setOpenSections] = useState({
        genel: true,
        eposta: true,
        olaylar: true,
        sablonlar: false,
        portal: false,
    });

    const load = useCallback(async () => {
        try {
            const data = await araciMerkez.ayarlar();
            setAyarlar(data.ayarlar || {});
            setStats(data.istatistik || {});
        } catch {
            toast.error("Aracı ayarları yüklenemedi.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const patchField = (field, value) => {
        setAyarlar((p) => ({ ...p, [field]: value }));
    };

    const save = async () => {
        if (!ayarlar) return;
        setSaving(true);
        try {
            const res = await araciMerkez.ayarlarKaydet(ayarlar);
            setAyarlar(res.ayarlar || ayarlar);
            toast.success("Aracı Merkezi ayarları kaydedildi.");
            onSaved?.();
        } catch {
            toast.error("Kaydetme başarısız.");
        } finally {
            setSaving(false);
        }
    };

    const bulkAction = async (action) => {
        setBulkBusy(action);
        try {
            const res = await araciMerkez.topluEmail({ action });
            if (action === "domain_email_kapat") {
                toast.success(`${res.guncellenen} aracıda e-posta kapatıldı + domain engeli açıldı.`);
                if (res.ayarlar) setAyarlar(res.ayarlar);
            } else if (action === "tamamen_kapat") {
                toast.success(
                    `Tüm aracı e-postaları kapatıldı (${res.guncellenen} aracı). Global SMTP kapalı.`,
                );
                if (res.ayarlar) setAyarlar(res.ayarlar);
            } else {
                toast.success(`${res.guncellenen} aracı güncellendi.`);
            }
            load();
        } catch {
            toast.error("Toplu işlem başarısız.");
        } finally {
            setBulkBusy("");
        }
    };

    const toggleSection = (id) => {
        setOpenSections((p) => ({ ...p, [id]: !p[id] }));
    };

    if (loading) {
        return (
            <div className="rounded-2xl border border-[#D4AF37]/20 bg-[#0B0F19]/60 p-6 text-center text-slate-500">
                <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
                Komuta ayarları yükleniyor…
            </div>
        );
    }

    if (!ayarlar) return null;

    const masterOff = !ayarlar.otomasyon_aktif;
    const emailOff = !ayarlar.bildirim_email;

    return (
        <section className="relative overflow-hidden rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#1a1510] via-[#0f1424] to-[#121A2F]">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(212,175,55,0.08),transparent_55%)] pointer-events-none" />

            <div className="relative p-5 border-b border-[#D4AF37]/15">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                    <div>
                        <div className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/35 bg-[#D4AF37]/10 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-[#F3E5AB] mb-2">
                            <Settings2 className="h-3.5 w-3.5" />
                            Komuta Ayarları
                        </div>
                        <h3 className="text-base font-bold text-white flex items-center gap-2">
                            <Sparkles className="h-4 w-4 text-[#D4AF37]" />
                            Aracı bildirim & otomasyon kontrol merkezi
                        </h3>
                        <p className="text-xs text-slate-500 mt-1 max-w-2xl">
                            Tüm aracı e-posta, panel bildirimi ve otomasyon kurallarını buradan yönetin.
                            @mbshukuk.com adresleri panel girişi içindir — e-posta engeli bounce sorununu çözer.
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2 shrink-0">
                        <button
                            type="button"
                            onClick={save}
                            disabled={saving}
                            className="h-9 px-4 rounded-xl bg-[#D4AF37] text-black text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-50"
                        >
                            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                            Kaydet
                        </button>
                    </div>
                </div>

                {stats && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-2 mt-4">
                        <StatPill label="Aktif aracı" value={stats.aktif_araci ?? 0} />
                        <StatPill label="E-posta açık" value={stats.bildirim_email_acik ?? 0} />
                        <StatPill
                            label="@mbshukuk.com"
                            value={stats.mbshukuk_domain_sayisi ?? 0}
                            warn={(stats.mbshukuk_email_acik ?? 0) > 0 && !ayarlar.mbshukuk_domain_mail_engelle}
                        />
                        <StatPill
                            label="Domain mail açık"
                            value={stats.mbshukuk_email_acik ?? 0}
                            warn={(stats.mbshukuk_email_acik ?? 0) > 0}
                        />
                        <StatPill label="Gmail tipi" value={stats.gmail_tipi_sayisi ?? 0} />
                    </div>
                )}

                {masterOff && (
                    <div className="mt-3 rounded-xl border border-rose-500/35 bg-rose-500/10 px-4 py-2.5 text-xs text-rose-200 flex items-center gap-2">
                        <MailX className="h-4 w-4 shrink-0" />
                        Ana otomasyon kapalı — hiçbir aracı bildirimi oluşturulmaz.
                    </div>
                )}
                {emailOff && !masterOff && (
                    <div className="mt-3 rounded-xl border border-emerald-500/35 bg-emerald-500/10 px-4 py-2.5 text-xs text-emerald-100 flex items-center gap-2">
                        <MailX className="h-4 w-4 shrink-0" />
                        Aracı e-postaları <strong className="font-semibold">KAPALI</strong> — hiçbir aracıya SMTP
                        gitmez. Açmak için &quot;E-posta bildirimleri (global)&quot; kutusunu işaretleyip Kaydet.
                    </div>
                )}
            </div>

            <div className="relative p-5 space-y-3">
                <div className="rounded-xl border border-amber-500/25 bg-amber-500/8 p-4">
                    <div className="text-xs font-semibold text-amber-200 mb-2 flex items-center gap-1.5">
                        <Zap className="h-3.5 w-3.5" />
                        Hızlı işlemler
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button
                            type="button"
                            disabled={!!bulkBusy}
                            onClick={() => bulkAction("tamamen_kapat")}
                            className="h-8 px-3 rounded-lg border border-rose-500/50 bg-rose-500/20 text-[10px] font-semibold text-rose-100 hover:bg-rose-500/30 disabled:opacity-50 inline-flex items-center gap-1"
                        >
                            {bulkBusy === "tamamen_kapat" ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                                <MailX className="h-3 w-3" />
                            )}
                            Tüm aracı e-postalarını kapat (hemen)
                        </button>
                        <button
                            type="button"
                            disabled={!!bulkBusy}
                            onClick={() => bulkAction("domain_email_kapat")}
                            className="h-8 px-3 rounded-lg border border-amber-500/40 bg-amber-500/15 text-[10px] font-medium text-amber-100 hover:bg-amber-500/25 disabled:opacity-50 inline-flex items-center gap-1"
                        >
                            {bulkBusy === "domain_email_kapat" ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                                <MailX className="h-3 w-3" />
                            )}
                            @mbshukuk.com e-postalarını kapat + engelle
                        </button>
                        <button
                            type="button"
                            disabled={!!bulkBusy}
                            onClick={() => bulkAction("tum_email_kapat")}
                            className="h-8 px-3 rounded-lg border border-white/10 text-[10px] text-slate-300 hover:bg-white/5 disabled:opacity-50"
                        >
                            Tüm aracılarda e-postayı kapat
                        </button>
                        <button
                            type="button"
                            disabled={!!bulkBusy}
                            onClick={() => patchField("bildirim_email", false)}
                            className="h-8 px-3 rounded-lg border border-white/10 text-[10px] text-slate-300 hover:bg-white/5"
                        >
                            Global SMTP kapat
                        </button>
                        <button
                            type="button"
                            disabled={!!bulkBusy}
                            onClick={() => {
                                patchField("otomasyon_aktif", true);
                                patchField("bildirim_panel", true);
                                patchField("mbshukuk_domain_mail_engelle", true);
                            }}
                            className="h-8 px-3 rounded-lg border border-emerald-500/30 text-[10px] text-emerald-300 hover:bg-emerald-500/10"
                        >
                            Güvenli mod (panel açık, domain mail kapalı)
                        </button>
                    </div>
                </div>

                {SECTIONS.map((sec) => {
                    const Icon = sec.icon;
                    const open = openSections[sec.id];
                    return (
                        <div
                            key={sec.id}
                            className="rounded-xl border border-white/10 bg-[#0B0F19]/50 overflow-hidden"
                        >
                            <button
                                type="button"
                                onClick={() => toggleSection(sec.id)}
                                className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-white/[0.03] transition"
                            >
                                <span className="flex items-center gap-2 text-sm font-semibold text-slate-100">
                                    <Icon className="h-4 w-4 text-[#D4AF37]" />
                                    {sec.title}
                                    <span className="text-[10px] font-normal text-slate-500">
                                        ({sec.toggles.filter(([k]) => ayarlar[k]).length}/{sec.toggles.length} açık)
                                    </span>
                                </span>
                                {open ? (
                                    <ChevronUp className="h-4 w-4 text-slate-500" />
                                ) : (
                                    <ChevronDown className="h-4 w-4 text-slate-500" />
                                )}
                            </button>
                            {open && (
                                <div className="px-4 pb-4 grid gap-2 sm:grid-cols-2">
                                    {sec.toggles.map(([field, label, hint]) => (
                                        <ToggleRow
                                            key={field}
                                            field={field}
                                            label={label}
                                            hint={hint}
                                            ayarlar={ayarlar}
                                            onChange={patchField}
                                            accent={sec.accent}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}

                <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-white/10">
                    <p className="text-[10px] text-slate-500 flex items-center gap-3">
                        <span className="inline-flex items-center gap-1">
                            <DollarSign className="h-3 w-3" /> Hakediş
                        </span>
                        <span className="inline-flex items-center gap-1">
                            <FileText className="h-3 w-3" /> Lead
                        </span>
                        <span className="inline-flex items-center gap-1">
                            <Users className="h-3 w-3" /> Portal
                        </span>
                        <span className="inline-flex items-center gap-1">
                            <Bot className="h-3 w-3" /> Akıl
                        </span>
                    </p>
                    <button
                        type="button"
                        onClick={save}
                        disabled={saving}
                        className="h-8 px-4 rounded-lg border border-[#D4AF37]/40 text-xs text-[#F3E5AB] hover:bg-[#D4AF37]/10 disabled:opacity-50 inline-flex items-center gap-1"
                    >
                        {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                        Değişiklikleri kaydet
                    </button>
                </div>
            </div>
        </section>
    );
}
